<template>
  <BaseDialog
    title="會員中心 QRcode 設定"
    width="610px"
    :btn1Loading="loading"
    @close="$emit('close')"
    @confirm="onConfirm"
  >
    <BaseElForm
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-position="top"
    >
      <!-- 是否啟用 -->
      <BaseElFormItem label="是否啟用" prop="enable">
        <BaseElSwitch
          v-model="formData.enable"
          active-text="開啟"
          inactive-text="關閉"
        />
      </BaseElFormItem>

      <!-- 選擇會員卡來源 -->
      <BaseElFormItem label="選擇會員卡來源" prop="cardSource">
        <BaseElSelect
          v-model="formData.cardSource"
          placeholder="請選擇會員卡來源"
          @change="onCardSourceChange"
        >
          <BaseElSelectOption
            v-for="option in memberCardSourceOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </BaseElSelect>
      </BaseElFormItem>

      <!-- 內部系統：會員條碼內容 -->
      <BaseElFormItem
        v-if="formData.cardSource === 'internal'"
        label="會員條碼內容"
        prop="qrcodeContent"
      >
        <BaseElSelect
          v-model="formData.qrcodeContent"
          placeholder="請選擇會員條碼內容"
        >
          <BaseElSelectOption
            v-for="option in defaultQRCodeContentOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </BaseElSelect>
      </BaseElFormItem>

      <!-- 外部系統：外部系統廠商 -->
      <BaseElFormItem
        v-if="formData.cardSource === 'external'"
        label="外部系統廠商"
        prop="externalType"
      >
        <BaseElSelect
          v-model="formData.externalType"
          placeholder="請選擇外部系統廠商"
        >
          <BaseElSelectOption
            v-for="option in qrcodeSourceOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </BaseElSelect>
      </BaseElFormItem>

      <!-- 外部系統：會員條碼類型 -->
      <BaseElFormItem
        v-if="formData.cardSource === 'external'"
        label="會員條碼類型"
        prop="type"
      >
        <BaseElSelect
          v-model="formData.type"
          placeholder="請選擇會員條碼類型"
        >
          <BaseElSelectOption label="QR Code" value="qrcode" />
        </BaseElSelect>
      </BaseElFormItem>
    </BaseElForm>
  </BaseDialog>
</template>

<script>
import { defineComponent, computed, onMounted } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { useShop } from '@/use/shop'
import { useBaseForm, onFormRulesChangeClearValidate } from '@/use/useForm'
import { noEmptyRules } from '@/validation'
import { UpdateMemberCenterQRCode } from '@/api/member/memberCenter'
import { defaultQRCodeContentOptions, memberCardSourceOptions } from '@/config/memberCenter'
import { get } from 'lodash'

export default defineComponent({
  name: 'MemberCenterQRCodeSettingEditModal',
  components: {
    BaseDialog,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
    qrcodeSourceOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formData, formRef, initFormData, loading, checkForm } = useBaseForm()

    // 動態表單驗證規則
    const formRules = computed(() => {
      const rules = {
        cardSource: [noEmptyRules()],
      }

      if (formData.cardSource === 'internal') {
        rules.qrcodeContent = [noEmptyRules()]
      }

      if (formData.cardSource === 'external') {
        rules.externalType = [noEmptyRules()]
        rules.type = [noEmptyRules()]
      }

      return rules
    })

    // 當驗證規則變更時清除驗證錯誤
    onFormRulesChangeClearValidate(formRef, formRules, 100)

    // 根據 configData 取得初始會員卡來源
    const getInitialCardSource = () => {
      return get(props.configData, 'isExternal', false) ? 'external' : 'internal'
    }

    // 根據 configData 取得初始會員條碼內容
    const getInitialQrcodeContent = () => {
      const scope = get(props.configData, 'defaultScope')
      const key = get(props.configData, 'defaultKey')
      const found = defaultQRCodeContentOptions.find(
        opt => opt.scope === scope && opt.key === key,
      )
      return found ? found.value : defaultQRCodeContentOptions[0].value
    }

    // 當會員卡來源變更時重置對應欄位
    const onCardSourceChange = () => {
      if (formData.cardSource === 'internal') {
        formData.qrcodeContent = defaultQRCodeContentOptions[0].value
        formData.externalType = ''
      } else {
        formData.qrcodeContent = ''
        formData.externalType = props.qrcodeSourceOptions[0]?.value || ''
      }
    }

    // 提交表單
    const onConfirm = async () => {
      if (loading.value) return
      loading.value = true

      const formPass = await checkForm(formRef.value)
      if (!formPass) {
        loading.value = false
        return
      }

      // 組裝 API 參數
      const isExternal = formData.cardSource === 'external'
      let defaultScope = ''
      let defaultKey = ''

      if (!isExternal) {
        const selectedContent = defaultQRCodeContentOptions.find(
          opt => opt.value === formData.qrcodeContent,
        )
        if (selectedContent) {
          defaultScope = selectedContent.scope
          defaultKey = selectedContent.key
        }
      }

      const [, err] = await UpdateMemberCenterQRCode({
        shopId: shopId.value,
        enable: formData.enable,
        type: formData.type,
        isExternal,
        externalType: isExternal ? formData.externalType : undefined,
        defaultScope: defaultScope || undefined,
        defaultKey: defaultKey || undefined,
      })

      loading.value = false

      if (err) {
        window.$message.error(err)
        return
      }

      window.$message.success('更新成功')
      emit('refresh')
      emit('close')
    }

    // 初始化表單資料
    onMounted(() => {
      initFormData({
        enable: get(props.configData, 'enable', false),
        cardSource: getInitialCardSource(),
        qrcodeContent: getInitialQrcodeContent(),
        externalType: get(props.configData, 'externalType', ''),
        type: get(props.configData, 'type', 'qrcode'),
      })
    })

    return {
      formData,
      formRef,
      formRules,
      loading,
      defaultQRCodeContentOptions,
      memberCardSourceOptions,
      onCardSourceChange,
      onConfirm,
    }
  },
})
</script>

<style lang="postcss" scoped>
</style>
